/*
 *   This content is licensed according to the W3C Software License at
 *   https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 *
 *   Supplemental JS for the disclosure menu keyboard behavior
 */

"use strict";

class DisclosureNav {
    constructor(domNode) {
        this.rootNode = domNode;
        this.controlledNodes = [];
        this.openIndex = null;
        this.useArrowKeys = true;
        this.topLevelNodes = [
            ...this.rootNode.querySelectorAll("[data-topLevel]"),
        ];

        this.topLevelNodes.forEach((node) => {
            // handle button + menu
            if (
                node.tagName.toLowerCase() === "button" && node.hasAttribute("aria-controls")
            ) {
                const menu = node.parentNode.querySelector("ul");
                if (menu) {
                    // save ref controlled menu
                    this.controlledNodes.push(menu);

                    // collapse menus
                    node.setAttribute("aria-expanded", "false");
                    this.toggleMenu(menu, false);

                    // attach event listeners
                    menu.addEventListener(
                        "keydown",
                        this.onMenuKeyDown.bind(this)
                    );
                    node.addEventListener(
                        "click",
                        this.onButtonClick.bind(this)
                    );
                    node.addEventListener(
                        "keydown",
                        this.onButtonKeyDown.bind(this)
                    );
                }
            }
            // handle links
            else {
                this.controlledNodes.push(null);
                node.addEventListener("keydown", this.onLinkKeyDown.bind(this));
            }
        });

        this.rootNode.addEventListener("focusout", this.onBlur.bind(this));
    }

    controlFocusByKey(keyboardEvent, nodeList, currentIndex) {
        switch (keyboardEvent.key) {
            case "ArrowUp":
            case "ArrowLeft":
                keyboardEvent.preventDefault();
                if (currentIndex > -1) {
                    var prevIndex = Math.max(0, currentIndex - 1);
                    nodeList[prevIndex].focus();
                }
                break;
            case "ArrowDown":
            case "ArrowRight":
                keyboardEvent.preventDefault();
                if (currentIndex > -1) {
                    var nextIndex = Math.min(
                        nodeList.length - 1,
                        currentIndex + 1
                    );
                    nodeList[nextIndex].focus();
                }
                break;
            case "Home":
                keyboardEvent.preventDefault();
                nodeList[0].focus();
                break;
            case "End":
                keyboardEvent.preventDefault();
                nodeList[nodeList.length - 1].focus();
                break;
        }
    }

    // public function to close open menu
    close() {
        this.toggleExpand(this.openIndex, false);
    }

    onBlur(event) {
        var menuContainsFocus = this.rootNode.contains(event.relatedTarget);
        if (!menuContainsFocus && this.openIndex !== null) {
            this.toggleExpand(this.openIndex, false);
        }
    }

    onButtonClick(event) {
        var button = event.target;
        var buttonIndex = this.topLevelNodes.indexOf(button);
        var buttonExpanded = button.getAttribute("aria-expanded") === "true";
        this.toggleExpand(buttonIndex, !buttonExpanded);
    }

    onButtonKeyDown(event) {
        var targetButtonIndex = this.topLevelNodes.indexOf(
            document.activeElement
        );

        // close on escape
        if (event.key === "Escape") {
            this.toggleExpand(this.openIndex, false);
        }

        // move focus into the open menu if the current menu is open
        else if (
            this.useArrowKeys &&
            this.openIndex === targetButtonIndex &&
            event.key === "ArrowDown"
        ) {
            event.preventDefault();
            this.controlledNodes[this.openIndex].querySelector("a").focus();
        }

        // handle arrow key navigation between top-level buttons, if set
        else if (this.useArrowKeys) {
            this.controlFocusByKey(
                event,
                this.topLevelNodes,
                targetButtonIndex
            );
        }
    }

    onLinkKeyDown(event) {
        var targetLinkIndex = this.topLevelNodes.indexOf(
            document.activeElement
        );

        // handle arrow key navigation between top-level buttons, if set
        if (this.useArrowKeys) {
            this.controlFocusByKey(event, this.topLevelNodes, targetLinkIndex);
        }
    }

    onMenuKeyDown(event) {
        if (this.openIndex === null) {
            return;
        }

        var menuLinks = Array.prototype.slice.call(
            this.controlledNodes[this.openIndex].querySelectorAll("a")
        );
        var currentIndex = menuLinks.indexOf(document.activeElement);

        // close on escape
        if (event.key === "Escape") {
            this.topLevelNodes[this.openIndex].focus();
            this.toggleExpand(this.openIndex, false);
        }

        // handle arrow key navigation within menu links, if set
        else if (this.useArrowKeys) {
            this.controlFocusByKey(event, menuLinks, currentIndex);
        }
    }

    toggleExpand(index, expanded) {
        // close open menu, if applicable
        if (this.openIndex !== index) {
            this.toggleExpand(this.openIndex, false);
        }

        // handle menu at called index
        if (this.topLevelNodes[index]) {
            this.openIndex = expanded ? index : null;
            this.topLevelNodes[index].setAttribute("aria-expanded", expanded);
            this.toggleMenu(this.controlledNodes[index], expanded);
        }
    }

    toggleMenu(domNode, show) {
        if (domNode) {
            const backdrop = document.getElementById('menuMain-backdrop')
            let submenuLinks = domNode.querySelectorAll('[role=menuitem]')
            if (show) {
                backdrop.classList.replace('opacity-0','opacity-25');
                domNode.classList.add('max-h-208');
                domNode.classList.remove('max-h-0');
                submenuLinks.forEach(e => {
                    e.setAttribute("tabindex", "0");
                });

                if (typeof this.openIndex === "number") {
                    this.topLevelNodes[this.openIndex].getElementsByTagName('svg')[0].classList.replace('rotate-0', 'rotate-180');
                }
            } else {
                backdrop.classList.replace('opacity-25','opacity-0');
                domNode.classList.add('max-h-0');
                domNode.classList.remove('max-h-208');
                submenuLinks.forEach(e => {
                    e.setAttribute("tabindex", "-1");
                });
                this.topLevelNodes.forEach((e) => {
                    const icon = e.getElementsByTagName('svg')[0];
                    if (icon) {
                        icon.classList.replace('rotate-180', 'rotate-0');
                    }
                })
            }
        }
    }

    updateKeyControls(useArrowKeys) {
        this.useArrowKeys = useArrowKeys;
    }
}

export { DisclosureNav };
